import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { motion, useAnimate, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { CampaignView } from "./campaign-view";
import {
  CampaignBanner,
  SegmentedTabs,
  type CampaignTab,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import { TabRowCloseButton } from "@/components/ui/glass";
import { useWidgetVariant } from "@/contexts/widget-variant";
import { useIsLandscapePhone } from "@/hooks/use-landscape-phone";
import { useIsDesktop } from "@/hooks/use-media-query";
import { CampaignInfoTab, CampaignTermsTab } from "./campaign-tab-bodies";
import { CampaignCountdown } from "@/components/campaign-card/campaign-countdown";
import { Typography } from "@/components/ui/typography";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { campaignKey } from "@/campaigns";
import {
  coverMorphSpring,
  frameloopFade,
  pageScaleSpring,
} from "@/utils/animations";
import { CampaignImage } from "@/components/campaign-card/campaign-image";
import { CampaignTabContext } from "./campaign-tab-context";
import { JoinNowButton } from "@/components/campaign-card/campaign-join-cta";
import { useWidget } from "@/hooks/widget";
import { cn } from "@/lib/utils";

import { useRestingOffset, useStickyIfFits } from "./use-sticky-if-fits";
import { OptInPromptContext } from "@/components/opt-in-prompt/opt-in-prompt-context";
import { OptInPromptFrame } from "@/components/opt-in-prompt/opt-in-prompt-layer";
import { useOptInPrompt } from "@/components/opt-in-prompt/use-opt-in-prompt";

/**
 * Global campaign layout shared by every campaign type.
 *
 * Structure is fixed — static left sidebar (banner + type slot) and a right
 * content card with the segmented tabs. Only what the `view` (from the registry)
 * puts in the sidebar / primary tab varies by campaign type.
 *
 * On desktop the sidebar stays put while the content card scrolls — but only when the sidebar
 * fits the scroll region as it stands (`useStickyIfFits`); a taller one scrolls with the page.
 */
/** The collapsed cover band (portrait in-game): its height, the circle the art becomes, and the
 *  circle's inset from the panel edge (the content's own 16px). */
const BAND_H = 56;
const AVATAR_D = 36;
const BAND_PAD_X = 16;

export const CampaignShell = ({ view }: { view: CampaignView }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<CampaignTab>("primary");
  const [asideRef, asideFits] = useStickyIfFits<HTMLElement>();
  // In-game on phones the panel has no header row: the cover leads the sticky block instead
  // (the on-site variant keeps its own WidgetHeader).
  const isInGame = useWidgetVariant() === "in-game";
  // Sideways the stack has no height: the cover becomes a header band and the content two columns.
  const landscapePhone = useIsLandscapePhone();
  const isLandscape = isInGame && landscapePhone;
  // Sideways the close sits inside the header row (no gutter for a button hanging off the corner).
  const { close: closeWidget } = useWidget();
  // ONE layout mounts, not both with the other hidden: the hidden desktop grid (leaderboard
  // table, sidebar cards, its own tab bar) was rendered, styled and laid out on every phone
  // commit — the work that stalled the tab pill.
  const isDesktop = useIsDesktop();

  // The in-game campaign switch, as ONE page instance: one shell serves every campaign
  // (CampaignIndexRouter only swaps `view`), so instead of mounting a second copy and
  // crossfading (which re-created the cover + tabs and jittered), the whole mobile column —
  // cover, tabs and content — scales DOWN and fades out with the OLD view still shown, the view
  // swaps while it is invisible (scroll reset, tab back to Overview, cover changes unseen), then
  // the column scales back to 1 and fades in on a fast spring. A few percent of scale: depth,
  // not a zoom.
  const incomingKey = campaignKey(view.campaign);
  const [frozen, setFrozen] = useState<CampaignView | null>(null);
  const shownRef = useRef(view);
  const shownKeyRef = useRef(incomingKey);
  const [pageScope, animatePage] = useAnimate<HTMLDivElement>();

  // Portrait in-game: the cover gives way as the player reads. The sticky header's offset is
  // NEGATIVE by the cover's height minus a 56px band, so the header first scrolls up with the
  // page and then pins as that band + the tabs. The art has two states: the full cover, and a
  // 36px circle at the start of the band with the name and time left beside it. Once the header
  // pins it springs into the circle; back above half-way, it springs out again (see the scroll
  // listener for the hysteresis and the short-page guard).
  const collapsing = isInGame && !isLandscape && !isDesktop;
  const coverRef = useRef<HTMLDivElement>(null);
  const [coverCollapsed, setCoverCollapsed] = useState(false);
  const [avatar, setAvatar] = useState<{
    x: number;
    y: number;
    scale: number;
    inset: number;
    radius: number;
    range: number;
  } | null>(null);
  useLayoutEffect(() => {
    const cover = coverRef.current;
    const column = pageScope.current;
    if (!collapsing || !cover || !column) return;
    // The circle state, from the cover's size: a uniform scale to the circle's diameter, a move
    // from the cover's centre to the circle's, and a clip that rounds the art's centre square
    // (in the art's own, pre-scale coordinates).
    const apply = () => {
      const w = cover.offsetWidth;
      const h = cover.offsetHeight;
      if (!w || !h) return;
      column.style.setProperty("--cover-h", `${h}px`);
      setAvatar({
        x: BAND_PAD_X + AVATAR_D / 2 - w / 2,
        y: h - BAND_H / 2 - h / 2,
        scale: AVATAR_D / h,
        inset: Math.max(0, (w - h) / 2),
        radius: h / 2,
        range: h - BAND_H,
      });
    };
    apply();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(apply);
    observer.observe(cover);
    return () => observer.disconnect();
  }, [collapsing, pageScope]);
  const collapseRange = avatar?.range ?? 0;
  useEffect(() => {
    const scroller = coverRef.current?.closest<HTMLElement>(".touch-scroller");
    if (!collapsing || !scroller || !collapseRange) return;
    // Collapse once the header has pinned (the art is down to the band, so it morphs in place
    // instead of while still scrolling up), expand again under half-way: a wide band of
    // hysteresis. A page that cannot scroll all the way to the pin never collapses — on a page
    // just past the line, momentum and rubber-banding flipped it back and forth. Scroll events
    // only flip a flag at the crossing; the spring does the motion.
    const onScroll = () => {
      const y = scroller.scrollTop;
      const canPin =
        scroller.scrollHeight - scroller.clientHeight >= collapseRange;
      setCoverCollapsed((was) =>
        !canPin ? false : was ? y > collapseRange * 0.5 : y >= collapseRange - 1
      );
    };
    onScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [collapsing, collapseRange]);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (!isInGame || incomingKey === shownKeyRef.current) return;
    const outgoing = shownRef.current;
    setFrozen(outgoing);
    let cancelled = false;
    // Every await is raced against a timer a little longer than the animation: if the frameloop
    // is paused (hidden tab, a starved host frame) the animation promise never settles, and the
    // switch must still complete.
    const settle = (
      anim: { then: (onResolve: VoidFunction) => unknown },
      ms: number
    ) =>
      Promise.race([
        new Promise<void>((r) => anim.then(r)),
        new Promise<void>((r) => setTimeout(r, ms)),
      ]);
    (async () => {
      // The SCROLLER (panel-sized) is what scales and fades, not the column inside it: the
      // column is thousands of pixels tall, and a transform on it meant a layer that size
      // re-rasterised every frame on phones. Same picture — everything visible is inside it.
      const el =
        pageScope.current?.closest<HTMLElement>(".touch-scroller") ??
        pageScope.current;
      if (el) {
        await settle(
          animatePage(
            el,
            { opacity: 0, scale: 0.96 },
            {
              duration: reducedMotion ? 0 : 0.16,
              ease: [0.4, 0, 1, 1],
              ...frameloopFade,
            }
          ),
          220
        );
      }
      if (cancelled) return;
      shownKeyRef.current = incomingKey;
      setFrozen(null);
      setTab("primary");
      el?.closest<HTMLElement>(".touch-scroller")?.scrollTo({ top: 0 });
      if (el) {
        await settle(
          animatePage(el, { opacity: 0, scale: 0.96 }, { duration: 0 }),
          50
        );
        if (cancelled) return;
        await settle(
          animatePage(
            el,
            { opacity: 1, scale: 1 },
            reducedMotion
              ? { duration: 0 }
              : {
                  scale: pageScaleSpring,
                  opacity: {
                    duration: 0.16,
                    ease: "easeOut",
                    ...frameloopFade,
                  },
                }
          ),
          600
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [incomingKey, isInGame, animatePage, pageScope, reducedMotion]);
  // What the column renders: the outgoing view while hinging out, the live view otherwise.
  // `shownRef` only ever tracks the view of the campaign currently on screen — on the render
  // that brings a NEW campaign it must keep the old one, or the scale-out would already show
  // the new page (the freeze happens in the effect, one tick after this render).
  const shown =
    frozen ?? (incomingKey === shownKeyRef.current ? view : shownRef.current);
  if (!frozen && incomingKey === shownKeyRef.current) shownRef.current = view;
  const currentKey = campaignKey(shown.campaign);
  const [tabsRef, tabsRestingOffset] = useRestingOffset<HTMLDivElement>();

  // The opt-in prompt: over the Overview of a campaign the player has not joined, the first time
  // they open it (see useOptInPrompt). It follows the campaign the column is SHOWING (the outgoing
  // one while a switch hinges out), and steps aside on Info / T&C so the rules stay readable —
  // the page's own Opt In is back there — returning on Overview until the player answers.
  const promptView = isDesktop ? view : shown;
  const promptCampaign = promptView.campaign;
  const prompt = useOptInPrompt(promptCampaign, promptView.canJoin);
  const promptShowing = prompt.active && tab === "primary";
  const desktopContentRef = useRef<HTMLElement>(null);
  // While it shows, the page under it is pinned at the top: the layer covers the body, so a
  // scroll would only move content nobody can see (or reach — it is inert).
  useEffect(() => {
    if (!promptShowing) return;
    const scroller = (
      pageScope.current ?? desktopContentRef.current
    )?.closest<HTMLElement>(".touch-scroller");
    if (!scroller) return;
    scroller.scrollTo({ top: 0 });
    const previous = scroller.style.overflowY;
    scroller.style.overflowY = "hidden";
    return () => {
      scroller.style.overflowY = previous;
    };
  }, [promptShowing, pageScope]);
  const promptFrame = (
    layout: "stack" | "landscape" | "desktop",
    className: string | undefined,
    children: ReactNode
  ) => (
    <OptInPromptFrame
      campaign={promptCampaign}
      active={promptShowing}
      phase={prompt.phase}
      onJoin={prompt.join}
      onDismiss={prompt.dismiss}
      layout={layout}
      className={className}
    >
      {children}
    </OptInPromptFrame>
  );

  // The terms (and the contribution breakdown under them) hang off the shell rather than each
  // type's view, because they are campaign-level facts with no type-specific rendering — the
  // same reason the banner lives here.
  const body =
    tab === "primary" ? (
      view.primary
    ) : tab === "info" ? (
      view.info
    ) : (
      <CampaignTermsTab campaign={view.campaign} />
    );

  // The mobile column renders `shown` (the outgoing view while hinging out), so its tab body
  // is derived from that, not from the live `view` the desktop layout uses.
  const mobileBody =
    tab === "primary" ? (
      shown.primary
    ) : tab === "info" ? (
      // Sideways the guide and the module's Info stack become columns (`ls-grid-children`).
      <CampaignInfoTab view={shown} />
    ) : (
      <CampaignTermsTab campaign={shown.campaign} />
    );

  return (
    <CampaignTabContext.Provider value={setTab}>
      <OptInPromptContext.Provider
        value={promptShowing ? campaignKey(promptCampaign) : null}
      >
        <div className="flex h-full w-full flex-col gap-4">
          {view.notice}
          <div
            className={cn(
              "min-h-full w-full",
              view.dimContent && "opacity-60 saturate-[0.7]"
            )}
          >
            {/* Desktop: static sidebar + tabbed content card. `min-h-full`, not `h-full`: the row is the
              sticky sidebar's containing block, so it must grow with the content card — a
              viewport-tall row would drag the sidebar away as soon as the row scrolled out. */}
            {isDesktop && (
              <div className="flex min-h-full w-full items-start gap-4">
                {/* `top-0` lands 16px under the scrollport edge (the region's own padding), i.e.
                exactly where the sidebar already sits — it never creeps up when pinned. */}
                <aside
                  ref={asideRef}
                  className={cn(
                    "flex w-97.5 shrink-0 flex-col gap-3 overflow-hidden",
                    asideFits && "sticky top-0"
                  )}
                >
                  <CampaignBanner
                    campaign={view.campaign}
                    finished={view.finished}
                  />
                  {view.sidebar}
                </aside>
                {/* The column's frame: a glass WELL, one step darker than the cards (a panel in
                the card colour made the cards inside it vanish into it), so the cards read on it
                and the column still reads as one region beside the sidebar. */}
                <section
                  ref={desktopContentRef}
                  className="glass-card glass-well flex min-w-0 flex-1 flex-col items-center gap-4 rounded-(--border-radius) p-4"
                >
                  {/* The tab bar stays put while the cards scroll under it, pinned exactly where it rests
                (`top-0` lands at the region's 16px padding, i.e. its own resting place — no slide
                before it sticks). It carries the well's flat fill across the well's full width
                (the negative margins undo the well's padding), keeps air under the pills so no card
                ever touches them, and its `before` paints the strip above it — exactly as tall as the bar's
                own resting offset from the scrollport, measured (the on-site page and the in-game
                embed put different things above it) — in the page colour, so scrolled cards never
                show above the bar. (The well has no rim, so nothing sits in the row the strip
                ends on.) */}
                  <div
                    ref={tabsRef}
                    className="sticky top-0 z-10 -mx-4 -mt-4 flex w-[calc(100%+2rem)] justify-center rounded-t-(--border-radius) bg-(--well-fill) px-4 pt-4 pb-3 before:absolute before:inset-x-0 before:top-[calc(-1*var(--region-pad-top))] before:h-(--region-pad-top) before:bg-background before:content-['']"
                    style={
                      {
                        "--region-pad-top": `${tabsRestingOffset}px`,
                      } as CSSProperties
                    }
                  >
                    <SegmentedTabs
                      tab={tab}
                      onChange={setTab}
                      primaryLabel={view.primaryLabel}
                    />
                  </div>
                  {tab === "primary"
                    ? promptFrame(
                        "desktop",
                        "flex w-full flex-col items-center gap-4",
                        body
                      )
                    : body}
                </section>
              </div>
            )}

            {/* Mobile: stacked; sidebar content folds into the "Overview" tab.
              -mt-4 cancels the scroll container's p-4 top padding so this column
              (the sticky bar's containing block) reaches the scrollport top —
              otherwise `sticky top-0` is clamped 16px down and content scrolls
              visible through the strip above the tabs. */}
            {!isDesktop && (
              <motion.div
                ref={pageScope}
                className={cn(
                  "relative -mt-4 flex min-h-[calc(100%+1rem)] flex-col gap-2 ls:-mt-3 ls:min-h-[calc(100%+0.75rem)]",
                  // The two panes scroll inside themselves: the column is exactly as tall as the
                  // scrollport, so the page itself never scrolls on the landscape Overview.
                  tab === "primary" &&
                    isLandscape &&
                    shown.landscape &&
                    "ls:h-[calc(100%+0.75rem)] ls:pb-3"
                )}
              >
                {/* Sticky header block, stuck to the top; z-20 keeps it above later siblings.
                In-game it is the campaign's art FULL-BLEED (edge to edge, tucked under the
                panel's rounded top) with the tab track breaking its bottom edge — half on the
                art, half below — so cover and tabs read as one composed header that stays on
                screen while the content scrolls under it. Close lives on the panel's own corner
                (WidgetContainer). On-site the header is the WidgetHeader, so only the tabs stick
                here (bg-background + pt-4 fill the strip above them) and the cover scrolls with
                the Overview content. */}
                <div
                  className={cn(
                    // `-top-4` on both: sticky offsets resolve against the scroller's padding edge, so
                    // -16px is the scrollport's actual top — the art sticks flush, no strip above it.
                    "sticky -top-4 z-20 flex flex-col bg-background pb-2 ls:-top-3",
                    !isInGame && "gap-3 pt-4"
                  )}
                  // Collapsing: pin only once the art is down to its bottom 56px.
                  style={
                    collapsing
                      ? { top: "calc(-1rem - var(--cover-h, 0px) + 3.5rem)" }
                      : undefined
                  }
                >
                  {isLandscape && (
                    /* Header (phone held sideways), ONE 44px row: the name, a chip with the time
                     left (or the type's headline figure), the tabs and the close — over the cover
                     blurred into a colour field. Every pixel of height here is taken from the
                     content, so nothing stacks and nothing hangs off the panel's corner. */
                    <div className="relative -mr-3 -ml-6 flex h-11 items-center gap-2.5 overflow-hidden pr-1.5 pl-6">
                      <div
                        className="absolute inset-0 -z-10 overflow-hidden"
                        aria-hidden="true"
                      >
                        <CampaignImage
                          key={currentKey}
                          campaign={shown.campaign}
                          hideTitle
                          hideDuration
                          className={cn(
                            "aspect-auto h-full w-full scale-125 rounded-none opacity-70 blur-lg saturate-[0.9]",
                            shown.finished && "grayscale"
                          )}
                        />
                        <div className="dock-cover-shade absolute inset-0" />
                      </div>
                      <Typography
                        tag="span"
                        className={cn(
                          "min-w-0 flex-1 truncate text-[13px] leading-tight font-bold tracking-wide",
                          shown.finished
                            ? "text-muted-foreground"
                            : "text-foreground"
                        )}
                      >
                        {campaignDisplayTitle(shown.campaign, t)}
                      </Typography>
                      <span className="flex h-6 shrink-0 items-center rounded-full border border-border px-2 font-heading text-[11px] text-foreground-secondary">
                        {shown.headerChip ?? (
                          <CampaignCountdown
                            campaign={shown.campaign}
                            variant="inline"
                            compact
                            className="text-[11px] [&_svg]:size-2.5"
                          />
                        )}
                      </span>
                      <SegmentedTabs
                        tab={tab}
                        onChange={setTab}
                        primaryLabel={t("nav.overview", "Overview")}
                        className="w-[216px] shrink-0 p-[3px]"
                      />
                      <TabRowCloseButton
                        onClick={closeWidget}
                        className="size-9 shrink-0"
                      />
                    </div>
                  )}
                  {isInGame && !isLandscape && (
                    <div
                      ref={coverRef}
                      className="relative -mx-4 aspect-[12/5] overflow-hidden"
                    >
                      {/* The art: the full cover, or the band's circle — springing between them. */}
                      <motion.div
                        className="absolute inset-0 origin-center"
                        initial={false}
                        animate={
                          coverCollapsed && avatar
                            ? {
                                x: avatar.x,
                                y: avatar.y,
                                scale: avatar.scale,
                                clipPath: `inset(0px ${avatar.inset}px round ${avatar.radius}px)`,
                              }
                            : {
                                x: 0,
                                y: 0,
                                scale: 1,
                                clipPath: "inset(0px 0px round 0px)",
                              }
                        }
                        transition={coverMorphSpring}
                      >
                        <CampaignImage
                          key={currentKey}
                          campaign={shown.campaign}
                          hideTitle
                          hideDuration
                          className={cn(
                            "aspect-auto h-full w-full rounded-none",
                            shown.finished && "opacity-70 grayscale"
                          )}
                        />
                      </motion.div>
                      {/* The band's label, beside the circle: the name and the time left, faded in
                        with the collapse. Right padding keeps the title clear of the close on the
                        panel corner. */}
                      <div
                        aria-hidden={!coverCollapsed}
                        className={cn(
                          "pointer-events-none absolute inset-x-0 bottom-0 flex h-14 items-center gap-2 pr-12 pl-[60px] transition-opacity duration-200 motion-reduce:transition-none",
                          coverCollapsed ? "opacity-100" : "opacity-0"
                        )}
                      >
                        <Typography
                          tag="span"
                          className={cn(
                            "min-w-0 truncate text-[14px] leading-tight font-bold tracking-wide",
                            shown.finished
                              ? "text-muted-foreground"
                              : "text-foreground"
                          )}
                        >
                          {campaignDisplayTitle(shown.campaign, t)}
                        </Typography>
                        <CampaignCountdown
                          campaign={shown.campaign}
                          variant="inline"
                          compact
                          className="shrink-0 text-[10px] text-foreground-secondary [&_svg]:size-2.5"
                        />
                      </div>
                    </div>
                  )}
                  {!isLandscape && (
                    <SegmentedTabs
                      tab={tab}
                      onChange={setTab}
                      primaryLabel={t("nav.overview", "Overview")}
                      // Just under the art, not on it: the cover stays whole (no fade, no overlap)
                      // and a tight 8px gap keeps cover + tabs reading as one header block.
                      className={cn("w-full", isInGame && "relative z-10 mt-2")}
                    />
                  )}
                </div>
                {shown.mobileNotice}
                {tab === "primary" && isLandscape && shown.landscape ? (
                  promptFrame(
                    "landscape",
                    "flex min-h-0 flex-1 flex-col",
                    <LandscapePanes
                      landscape={shown.landscape}
                      joinCta={
                        shown.canJoin && !promptShowing ? (
                          <JoinNowButton
                            campaign={shown.campaign}
                            size="compact"
                            className="cta-float-shadow w-full shrink-0"
                          />
                        ) : null
                      }
                    />
                  )
                ) : tab === "primary" ? (
                  promptFrame(
                    isLandscape ? "landscape" : "stack",
                    isLandscape ? "flex flex-1 flex-col" : undefined,
                    <div
                      className={
                        // Sideways the width is the resource: cards flow in two columns.
                        isLandscape
                          ? "grid flex-1 grid-cols-2 items-start gap-2"
                          : "flex flex-col gap-2"
                      }
                    >
                      {!isInGame && (
                        <CampaignBanner
                          campaign={shown.campaign}
                          finished={shown.finished}
                        />
                      )}
                      {shown.primary}
                      {shown.sidebar}
                    </div>
                  )
                ) : (
                  <div
                    className={cn(
                      // Info sideways: its cards flow into two columns. The terms are reading, not
                      // scanning: one centered column at a readable measure.
                      isLandscape &&
                        (tab === "terms"
                          ? "mx-auto w-full max-w-[500px]"
                          : "ls-grid-children")
                    )}
                  >
                    {mobileBody}
                  </div>
                )}
                {/* Room under the content for the in-game campaign sheet (--igw-dock-inset, 0 where
                there is no sheet). A real element, not scroll-container padding: end padding is
                not reliably part of the scrollable area across engines (Safari), so the last card
                could never rise clear of the sheet there. The floating opt-in, when there is one,
                takes that room itself (it clears the sheet by its own sticky offset). */}
                {/* The panes pin their own Opt In and fill the height exactly: no floating CTA and
                  no room-under-the-content spacer there. */}
                {!(tab === "primary" && isLandscape && shown.landscape) &&
                  (shown.mobileStickyAction ?? (
                    <div
                      aria-hidden="true"
                      className="h-[var(--igw-dock-inset,0px)] w-full shrink-0"
                    />
                  ))}
              </motion.div>
            )}
          </div>
        </div>
      </OptInPromptContext.Provider>
    </CampaignTabContext.Provider>
  );
};

/**
 * The landscape Overview: a fixed narrow column and a flexible wide one, each scrolling inside
 * itself (hidden scrollbar, as everywhere on phones). The Opt In, when there is one, is pinned at
 * the narrow column's foot — the player's own column — instead of floating over the content.
 */
const LandscapePanes = ({
  landscape,
  joinCta,
}: {
  landscape: NonNullable<CampaignView["landscape"]>;
  joinCta: ReactNode;
}) => {
  const narrow = (
    <div className="flex w-[228px] shrink-0 flex-col gap-2">
      {/* `[&>*]:shrink-0`: cards clip their overflow, and a clipping flex child would shrink to
          the pane instead of making it scroll. */}
      <div className="touch-scroller flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain [&>*]:shrink-0">
        {landscape.narrow}
      </div>
      {joinCta}
    </div>
  );
  const wide = (
    <div className="touch-scroller flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain [&>*]:shrink-0">
      {landscape.wide}
    </div>
  );
  const panes = (
    <div className="flex min-h-0 flex-1 gap-2.5">
      {landscape.narrowSide === "start" ? (
        <>
          {narrow}
          {wide}
        </>
      ) : (
        <>
          {wide}
          {narrow}
        </>
      )}
    </div>
  );
  return landscape.frame ? landscape.frame(panes) : panes;
};
